import { ALL_POWERUP_KEYS, DEFAULT_SPAWN_CHANCE, DEFAULT_WORM_SPEED } from "../../shared/powerups";
import { PLAYSTYLES, playstyleSpawnChance } from "core/src/playstyles.js";

// Fun one-click match flavors. Each sets spawn rate, worm speed and which
// power-ups are enabled (the rest are disabled). "Changes all settings below."
export interface Preset {
  name: string;
  icon: string; // Icon name
  color: string;
  spawn: number;
  speed: number;
  enabled: string[] | "all"; // power-up keys ON; "all" = every power-up
}

// Presentation (icon + accent) per playstyle, keyed by name. The GAMEPLAY (enabled
// power-ups + spawn rate) is the shared source of truth in core/src/playstyles.js,
// so the setup panel, the lobby chip and the server all agree on what each flavor is.
const PRESENTATION: Record<string, { icon: string; color: string }> = {
  "Classic": { icon: "trophy", color: "#f5c542" },
  "Identity crisis": { icon: "users", color: "#b06cff" },
  "Sabotage": { icon: "flame", color: "#ff5c5c" },
  "Drink and drive": { icon: "beer", color: "#ff9f45" },
  "Fatboy Slim": { icon: "burger", color: "#7CFC66" },
};

export const NO_POWERUPS = "No powerups";
export const CUSTOM = "Custom";

// The two derived (non-preset) modes, for the in-game mode chip.
const DERIVED_PRESENTATION: Record<string, { icon: string; color: string }> = {
  [NO_POWERUPS]: { icon: "close", color: "#9aa7c7" },
  [CUSTOM]: { icon: "sliders", color: "#b06cff" },
};

export const PRESETS: Preset[] = PLAYSTYLES.map((ps) => ({
  name: ps.name,
  icon: PRESENTATION[ps.name]?.icon || "trophy",
  color: PRESENTATION[ps.name]?.color || "#f5c542",
  spawn: playstyleSpawnChance(ps.name),
  speed: DEFAULT_WORM_SPEED,
  enabled: ps.enabled as string[] | "all",
}));

// Icon + accent for a playstyle name (the lobby chip, the in-game mode chip).
// Falls back to Classic's.
export function playstylePresentation(name: string): { icon: string; color: string } {
  return PRESENTATION[name] || DERIVED_PRESENTATION[name] || PRESENTATION["Classic"];
}

// The disabledPowerUps list a preset implies.
export function presetDisabled(preset: Preset): string[] {
  if (preset.enabled === "all") return [];
  const on = new Set(preset.enabled);
  return ALL_POWERUP_KEYS.filter((k) => !on.has(k));
}

function sameSet(a: string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

// The playstyle a settings combo reads as. Matching is on the enabled power-up
// set EXACTLY (so a named playstyle always means the same power-ups), but spawn
// rate is free within a playstyle. Two special cases win first: spawn Off or
// nothing enabled -> "No powerups". No match -> "Custom".
export function derivePlaystyle(disabledPowerUps: string[], spawnChance: number): string {
  const disabled = new Set(disabledPowerUps);
  const enabled = ALL_POWERUP_KEYS.filter((k) => !disabled.has(k));
  if (spawnChance === 0 || enabled.length === 0) return NO_POWERUPS;
  for (const p of PRESETS) {
    const on = p.enabled === "all" ? ALL_POWERUP_KEYS : p.enabled;
    if (sameSet(enabled, on)) return p.name;
  }
  return CUSTOM;
}

// The playstyle a running match reads as, from its matchConfig (absent fields =
// the engine defaults, i.e. Classic). Works for every kind of match (public box,
// custom lobby, local) since it reads the power-up rules the match actually runs.
export function matchPlaystyle(matchConfig: { disabledPowerUps?: string[]; powerUpSpawnChance?: number } | null | undefined): string {
  return derivePlaystyle(matchConfig?.disabledPowerUps || [], matchConfig?.powerUpSpawnChance ?? DEFAULT_SPAWN_CHANCE);
}
